import React, { useEffect, useMemo, useRef, useState } from 'react';
import LOGO from './../../../assets/img/MOSIDA-logo.png';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../redux/store';
import { setError, setLoading, setQrImageUrl, setType, setUploadedImageUrl, setUuid } from '../../../redux/slices/uploadFlowSlice';
import { generateImageQrCode } from '../../../services/qrCodeService';
import { Socket } from 'socket.io-client';
import { finishHobbyCaricaturePersonCapture } from '../../../utils/hobbyPersonFlow';
import {
    createImageQrSocket,
    extractUploadedImageUrl,
    getImageQrSecretKey,
    getImageQrSocketUrl,
} from '../../../config/imageQrSocket';
import {
    isAiSelfieCaricatureHobbyPostPaymentActive,
    isSnapPrintFlow,
    readSelectedAiSelfieArtFromStorage,
    resolveHobbyCaricatureRouteSlug,
    shouldUseHobbyCaricatureFlowForStoredAiSelfieArt,
} from '../../../services/stylePromptResolver';
import { store } from '../../../redux/store';
import { initHobbyPeopleSession, setHobbyGroupPhotoFlow } from '../../../redux/slices/uploadFlowSlice';
import FifaBg from './../../../assets/img/fifa/fifa-inner.png'
import { isFifaWorldCup2026Station, persistFifaSelfie, resolveFifaStationSlug, resolveStationRouteSlug } from '../../../utils/stationFlowStorage';
import { loadKioskTimeout } from '../../../redux/slices/kioskSlice';
import { DEFAULT_QR_EXPIRY_MS } from '../../../services/kioskTimeoutService';
import QrSessionExpiredModal from '../../../modal/QrSessionExpiredModal';
import StationThemeLayout from '../../../layouts/StationThemeLayout';

const formatQrExpiryCountdown = (ms: number) => {
    const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const QRCodeForImageUpload = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const dispatch = useDispatch<AppDispatch>();
    const { qrExpiryMs, loaded: kioskSettingsLoaded, loading: kioskSettingsLoading } = useSelector(
        (state: RootState) => state.kiosk
    );
    const qrExpiryDurationMs = qrExpiryMs > 0 ? qrExpiryMs : DEFAULT_QR_EXPIRY_MS;
    const [qrUrl, setQrUrl] = useState<string | null>(null);
    const [isLoadingLocal, setIsLoadingLocal] = useState<boolean>(true);
    const [qrImageLoaded, setQrImageLoaded] = useState(false);
    const [errorLocal, setErrorLocal] = useState<string | null>(null);
    const [showQrExpiredModal, setShowQrExpiredModal] = useState(false);
    const [qrExpiryRemainingMs, setQrExpiryRemainingMs] = useState<number | null>(null);
    const [qrExpiryEndsAt, setQrExpiryEndsAt] = useState<number | null>(null);
    const socketRef = useRef<Socket | null>(null);
    const uploadHandledRef = useRef(false);
    const qrExpiredHandledRef = useRef(false);
    const qrExpiryStartedRef = useRef(false);
    const isMountedRef = useRef(true);
    const hobbyGroupPhotoFlow = useSelector(
        (state: RootState) => state.uploadFlow.hobbyGroupPhotoFlow
    );
    const isGroupPhotoFlow =
        hobbyGroupPhotoFlow ||
        Boolean((location.state as { groupPhotoFlow?: boolean } | null)?.groupPhotoFlow);
    const uuid = useMemo(
        () =>
            (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
                ? crypto.randomUUID()
                : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`,
        []
    );

    const stationData = useMemo(() => {
        const selectedRaw = localStorage.getItem('selectedStation');
        const fromSelected = selectedRaw ? JSON.parse(selectedRaw) : null;
        const fromStorage = JSON.parse(localStorage.getItem('stationData') || '{}');
        return fromSelected || fromStorage;
    }, [location.state]);

    useEffect(() => {
        isMountedRef.current = true;
        dispatch(setType(stationData?.type))
        dispatch(setUuid(uuid))
        const cleanUuid = uuid.trim();

        const handleUploadedImage = (imageUrl: string, socket: Socket) => {
            if (uploadHandledRef.current || !isMountedRef.current) return;
            uploadHandledRef.current = true;
            dispatch(setType(stationData.type))
            socket.disconnect()
            const useHobbyCaricatureCapture =
                stationData.type === "hobby-caricature" ||
                isAiSelfieCaricatureHobbyPostPaymentActive() ||
                shouldUseHobbyCaricatureFlowForStoredAiSelfieArt();
            const hobbySlug = resolveHobbyCaricatureRouteSlug(
                resolveStationRouteSlug(
                    stationData,
                    undefined,
                    readSelectedAiSelfieArtFromStorage()
                )
            );
            const groupFlowActive =
                store.getState().uploadFlow.hobbyGroupPhotoFlow ||
                Boolean(
                    (location.state as { groupPhotoFlow?: boolean } | null)
                        ?.groupPhotoFlow
                );
            if (useHobbyCaricatureCapture && groupFlowActive) {
                dispatch(setHobbyGroupPhotoFlow(true));
                navigate(`/${hobbySlug}/extract-person`, {
                    replace: true,
                    state: {
                        groupImageUrl: imageUrl,
                        groupPhotoFlow: true,
                        stationData,
                    },
                });
            } else if (useHobbyCaricatureCapture) {
                const { hobbyPeopleCount } = store.getState().uploadFlow;
                if (hobbyPeopleCount == null) {
                    dispatch(initHobbyPeopleSession(1));
                }
                finishHobbyCaricaturePersonCapture(
                    navigate,
                    hobbySlug,
                    imageUrl
                );
            } else if (
                isFifaWorldCup2026Station(stationData, readSelectedAiSelfieArtFromStorage()) ||
                (location.state as { fifaUploadReturn?: boolean } | null)?.fifaUploadReturn
            ) {
                dispatch(setUploadedImageUrl(imageUrl));
                persistFifaSelfie(imageUrl);
                const fifaSlug = resolveFifaStationSlug(stationData);
                navigate(`/${fifaSlug}/upload-fifa-image`, {
                    replace: true,
                    state: { capturedImage: imageUrl },
                });
            } else {
                dispatch(setUploadedImageUrl(imageUrl))
                const artSlug = resolveStationRouteSlug(
                    stationData,
                    undefined,
                    readSelectedAiSelfieArtFromStorage()
                );
                navigate(`/${artSlug}/image-transformation`, { state: { stationData } })
            }
        };

        const onSocketImagePayload = (data: unknown, socket: Socket) => {
            console.log("QR upload payload received:", data);
            const imageUrl = extractUploadedImageUrl(data);
            if (imageUrl) {
                handleUploadedImage(imageUrl, socket);
            }
        };

        const run = async () => {
            setIsLoadingLocal(true)
            dispatch(setLoading(true))
            const selectedArt = readSelectedAiSelfieArtFromStorage();
            const snapPrint = isSnapPrintFlow({
                slug: resolveStationRouteSlug(stationData, undefined, selectedArt),
                stationType: stationData?.type,
                artName: selectedArt?.name,
            });
            const res = await generateImageQrCode(cleanUuid, {
                // Print Your Own: upload only — BG removal is decided later on /bg-remove.
                removeBg: snapPrint ? false : undefined,
            })
            if (!isMountedRef.current) return;
            if (!res.success || !res.data?.qrImageUrl) {
                const err = res.error || 'Failed to generate QR code'
                setErrorLocal(err)
                dispatch(setError(err))
                setIsLoadingLocal(false)
                dispatch(setLoading(false))
                return
            }
            setQrUrl(res.data.qrImageUrl!)
            dispatch(setQrImageUrl(res.data.qrImageUrl!))
            setIsLoadingLocal(false)
            dispatch(setLoading(false))

            try {
                const baseUrl = getImageQrSocketUrl()
                const secretKey = getImageQrSecretKey()

                const completeScanUrl = res.data.completeScanUrl || res.data.uploadUrl

                console.log('Environment check:')
                console.log('- Image QR socket URL:', baseUrl || 'NOT SET')
                console.log('- REACT_APP_IMAGE_QR_CODE_SECRET_KEY:', secretKey ? `${secretKey.substring(0, 10)}...` : 'NOT SET')
                console.log('- Image QR complete URL:', completeScanUrl || 'NOT SET')
                // Log URL alone so DevTools renders it as a clickable link
                if (completeScanUrl) {
                    console.log(completeScanUrl)
                }

                if (!baseUrl) {
                    const err = 'Image QR socket host is not configured (REACT_APP_MOSIDA_API_ORIGIN)'
                    setErrorLocal(err)
                    dispatch(setError(err))
                    return
                }

                if (!secretKey) {
                    const err = 'Image QR secret key is not configured (REACT_APP_IMAGE_QR_CODE_SECRET_KEY)'
                    setErrorLocal(err)
                    dispatch(setError(err))
                    return
                }

                if (!socketRef.current) {
                    const socket = createImageQrSocket(secretKey);

                    socketRef.current = socket;

                    const register = () => {
                        console.log("Socket connected:", socket.id);
                        console.log('Registering for upload events...', cleanUuid);
                        socket.emit("registerUser", { userId: cleanUuid });
                    };

                    socket.on("connect", register);
                    socket.on("reconnect", register);

                    socket.on(cleanUuid, (data) => onSocketImagePayload(data, socket));
                    // Generic fallbacks if backend emits a fixed event name
                    socket.on("qrCodeUrl", (data) => onSocketImagePayload(data, socket));
                    socket.on("imageUploaded", (data) => onSocketImagePayload(data, socket));
                    socket.on("image-uploaded", (data) => onSocketImagePayload(data, socket));

                    socket.on("registrationSuccess", (data) => {
                        console.log("Image QR registration success:", data);
                    });

                    socket.on("registrationError", (data) => {
                        console.error("Image QR registration error:", data);
                        if (!isMountedRef.current) return;
                        const err = 'Failed to register for image upload updates'
                        setErrorLocal(err)
                        dispatch(setError(err))
                    });

                    socket.on("disconnect", (reason) => {
                        console.log("Socket disconnected:", reason);
                    });

                    socket.on("connect_error", (error) => {
                        console.error("Socket connection error:", error);
                        if (!isMountedRef.current) return;
                        setErrorLocal('Socket connection error - check console for details')
                        dispatch(setError('Socket connection error'))
                    });
                }

            } catch (e) {
                console.error('Socket setup error:', e)
                if (!isMountedRef.current) return;
                const err = 'Socket connection failed'
                setErrorLocal(err)
                dispatch(setError(err))
            }
        }
        run()
        return () => {
            isMountedRef.current = false;
            if (socketRef.current) {
                socketRef.current.removeAllListeners();
                socketRef.current.disconnect();
                socketRef.current = null;
            }
        }
    }, [])
    useEffect(() => {
        if (!qrUrl) {
            setQrImageLoaded(false);
            return;
        }

        let cancelled = false;
        setQrImageLoaded(false);

        const img = new Image();
        img.onload = () => {
            if (!cancelled) setQrImageLoaded(true);
        };
        img.onerror = () => {
            if (!cancelled) setQrImageLoaded(true);
        };
        img.src = qrUrl;

        return () => {
            cancelled = true;
        };
    }, [qrUrl]);

    useEffect(() => {
        if (kioskSettingsLoaded || kioskSettingsLoading) return;
        dispatch(loadKioskTimeout());
    }, [dispatch, kioskSettingsLoaded, kioskSettingsLoading]);

    const handleQrExpiredModalHide = () => {
        setShowQrExpiredModal(false);
        navigate(-1);
    };

    useEffect(() => {
        if (!qrUrl || !qrImageLoaded) {
            qrExpiryStartedRef.current = false;
            setQrExpiryEndsAt(null);
            setQrExpiryRemainingMs(null);
            return;
        }

        if (qrExpiryStartedRef.current) return;

        const endsAt = Date.now() + qrExpiryDurationMs;
        qrExpiryStartedRef.current = true;
        setQrExpiryEndsAt(endsAt);

        const tick = () => {
            const remaining = endsAt - Date.now();
            setQrExpiryRemainingMs(remaining > 0 ? remaining : 0);
        };

        tick();
        const intervalId = window.setInterval(tick, 1000);
        return () => window.clearInterval(intervalId);
    }, [qrUrl, qrImageLoaded, qrExpiryDurationMs]);

    useEffect(() => {
        if (qrExpiryRemainingMs !== 0 || !qrExpiryEndsAt) return;
        if (uploadHandledRef.current || qrExpiredHandledRef.current) return;

        qrExpiredHandledRef.current = true;

        if (socketRef.current) {
            socketRef.current.removeAllListeners();
            socketRef.current.disconnect();
            socketRef.current = null;
        }

        setShowQrExpiredModal(true);
    }, [qrExpiryRemainingMs, qrExpiryEndsAt, uuid]);

    const isFifaUpload =
        isFifaWorldCup2026Station(stationData, readSelectedAiSelfieArtFromStorage()) ||
        (location.state as { fifaUploadReturn?: boolean } | null)?.fifaUploadReturn;
    const pageBackground = isFifaUpload ? FifaBg : undefined;

    return (
        <>
            <StationThemeLayout
                className="chooseOptionSection qrCodeForImageUploadSection p-0"
                station={stationData}
                backgroundOverride={pageBackground}
                showStationLogo={!isFifaUpload}
            >
                <div className='h-100 chooseCard'>
                    {isFifaUpload && (
                        <div className="mosidaLogoWhite">
                            <img src={LOGO} alt="MOSIDA logo" className="logo" />
                            <span className="badgeCreative">FIFA World Cup</span>
                        </div>
                    )}
                    <div className='chooseContainer'>
                        <div className='heading'>
                            {isGroupPhotoFlow ? 'Upload your group photo' : 'Upload your image '}
                        </div>
                        <div className='description'>
                            {isGroupPhotoFlow ? (
                                <>
                                    Scan with your phone to upload one group photo.
                                    <br />
                                    We&apos;ll extract each person automatically.
                                </>
                            ) : (
                                <>
                                    Scan to using your phone to upload your Image. <br />
                                    You will be redirected to MOSIDA Website upload
                                </>
                            )}
                        </div>
                        <div className='qrCodeImageSection mt-4'>
                            {!isLoadingLocal && !qrUrl ? (
                                <div style={{ color: '#c00', textAlign: 'center' }}>Failed to load QR code</div>
                            ) : (
                                <div className="qrCodeImageWrap">
                                    {qrImageLoaded && qrUrl ? (
                                        <img src={qrUrl} alt="qr" className='qrImgUrl' />
                                    ) : (
                                        <div className="qrCodeShimmer" aria-hidden="true" />
                                    )}
                                </div>
                            )}
                        </div>

                        {!qrImageLoaded ? (
                            <div className="qrExpiryTile mt-4" aria-hidden="true">
                                <div className="qrExpiryTimerShimmer" />
                            </div>
                        ) : qrExpiryEndsAt && (
                            <div className="qrExpiryTile mt-4" aria-live="polite">
                                <div className="qrExpiryTile__label">
                                    {qrExpiryRemainingMs !== null && qrExpiryRemainingMs > 0 && (
                                        <>
                                            <span>Expires in</span>
                                            <span className="ms-2" style={{ fontSize: "18px" }}>
                                                {formatQrExpiryCountdown(qrExpiryRemainingMs)}
                                            </span>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                        {errorLocal && <div style={{ color: '#c00', marginTop: 12, textAlign: 'center' }}>{errorLocal}</div>}
                    </div>
                </div>
            </StationThemeLayout>
            {showQrExpiredModal && (
                <QrSessionExpiredModal
                    show={showQrExpiredModal}
                    onHide={handleQrExpiredModalHide}
                    title="This upload QR has expired. Please try again."
                />
            )}
        </>
    );
}

export default QRCodeForImageUpload
